import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { GENDER_LABELS } from '../../model/people';
import type { GenderIdentity } from '../../model/types';
import { Menu, type MenuEntry } from './Menu';

/** Always in the main list. */
const PRIMARY: GenderIdentity[] = ['woman', 'man', 'nonbinary'];
/** One step away, in the "More identities" submenu, so the main list stays short. */
export const MORE_GENDERS: GenderIdentity[] = ['trans-woman', 'trans-man', 'transfeminine', 'transmasculine', 'genderfluid', 'genderqueer'];

const OPTION_LABEL: Partial<Record<GenderIdentity, string>> = { custom: 'Self-described…', unknown: 'Unknown / not recorded' };
const label = (g: GenderIdentity) => OPTION_LABEL[g] ?? GENDER_LABELS[g];

/** Gender selector: the common choices up front, more identities in a submenu. */
export function GenderPicker({
  value,
  onChange,
  id,
  ariaLabel,
  autoOpen,
  onClose,
  placeholder,
}: {
  value: GenderIdentity;
  onChange: (g: GenderIdentity) => void;
  id?: string;
  ariaLabel?: string;
  /** Open the menu right away (inline table editing). */
  autoOpen?: boolean;
  onClose?: () => void;
  /** Shown faintly in place of the value, e.g. "Mixed" when editing several people. */
  placeholder?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ x: number; y: number; w: number } | null>(null);
  const item = (g: GenderIdentity) => ({ key: g, label: label(g), checked: value === g, onSelect: () => onChange(g) });
  const items: MenuEntry[] = [
    ...PRIMARY.map(item),
    { key: 'more', label: 'More identities', checked: MORE_GENDERS.includes(value) || undefined, submenu: MORE_GENDERS.map(item) },
    'separator',
    item('custom'),
    item('unknown'),
  ];
  const open = () => {
    const r = ref.current!.getBoundingClientRect();
    setAt({ x: r.left, y: r.bottom + 4, w: r.width });
  };
  useEffect(() => {
    if (autoOpen) open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <button
        ref={ref}
        id={id}
        type="button"
        className="select gender-picker"
        aria-haspopup="menu"
        aria-expanded={!!at}
        aria-label={ariaLabel ? `${ariaLabel}: ${placeholder ?? label(value)}` : undefined}
        onClick={() => (at ? setAt(null) : open())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            open();
          }
        }}
      >
        <span className={placeholder ? 'faint' : undefined}>{placeholder ?? label(value)}</span>
        <ChevronDown size={14} />
      </button>
      {at && (
        <Menu
          items={items}
          x={at.x}
          y={at.y}
          minWidth={Math.max(at.w, 200)}
          ariaLabel="Gender"
          onClose={() => {
            setAt(null);
            ref.current?.focus();
            onClose?.();
          }}
        />
      )}
    </>
  );
}
